// apps/web/src/layouts/components/SidebarSupportBadge.tsx
//
// Badge dinâmica de chamados de suporte (ADMINISTRAÇÃO > Chamados). Espelha o
// <SidebarCommissionAttentionBadge>: conta os chamados aguardando o atendente
// (summary.open = aberto + em_atendimento). O conteúdo vive num componente
// interno renderizado só quando o usuário tem `support:manage` — assim a query
// nem monta para quem não pode atender (mesmo gate do grupo "chamados").
// Contrato: docs/contracts/ESCR-271-support-tickets.md.

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { supportTicketsKeys, useSocketEvent, useSupportTicketsSummary } from '@alamo/api';
import { usePermissions } from '@alamo/auth';

export interface SidebarSupportBadgeProps {
  variant?: 'highlighted' | 'muted';
  ariaLabel?: string;
}

export function SidebarSupportBadge({ variant, ariaLabel }: SidebarSupportBadgeProps) {
  const { hasPermission } = usePermissions();
  if (!hasPermission('support', 'manage')) return null;
  return <SidebarSupportBadgeInner variant={variant} ariaLabel={ariaLabel} />;
}

function SidebarSupportBadgeInner({
  variant = 'highlighted',
  ariaLabel,
}: SidebarSupportBadgeProps) {
  const { t } = useTranslation('support');
  const queryClient = useQueryClient();

  // Realtime: o backend emite `support.tickets.count_changed` (room
  // `admin:support-tickets`, auto-join) na abertura e em cada mudança de status.
  // Recontamos server-side via summary — nunca no front. useSocketEvent adquire
  // o socket compartilhado, então a assinatura mantém a conexão viva enquanto o
  // badge está montado.
  useSocketEvent('support.tickets.count_changed', () => {
    void queryClient.invalidateQueries({ queryKey: supportTicketsKeys.summary() });
  });

  const { data } = useSupportTicketsSummary();

  const total = data?.open ?? 0;
  if (total === 0) return null;

  const style =
    variant === 'highlighted' ? 'bg-amber-500 text-white' : 'bg-amber-100 text-amber-800';
  const label = ariaLabel ?? t('tickets.admin.badge_aria', { count: total });

  return (
    <span
      data-testid="sidebar-support-badge"
      title={label}
      aria-label={label}
      className={
        'inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 ' +
        'text-[10px] font-body font-semibold tabular-nums ' +
        style
      }
    >
      {total > 99 ? '99+' : total}
    </span>
  );
}
